import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { BowelMovement } from '../types';
import { AddBowelMovementForm } from '../components/BowelMovements/AddBowelMovementForm';
import { BowelMovementHistory } from '../BowelMovementHistory';
import { exportService } from '../lib/exportService';
import { ErrorWithRetry } from '../components/common/ErrorMessage';
import { getErrorMessage } from '../utils/errorMessages';

interface BowelMovementsProps {
  entries: BowelMovement[];
  onSubmit: (data: BowelMovement) => void;
  onUpdate: (id: string, data: BowelMovement) => void;
  onDelete: (id: string) => void;
  loadError?: Error | null;
  onRetry?: () => void;
}

const ReportIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

export const BowelMovements: React.FC<BowelMovementsProps> = ({
  entries,
  onSubmit,
  onUpdate,
  onDelete,
  loadError,
  onRetry,
}) => {
  const [historyFilter, setHistoryFilter] = useState<'all' | 'today' | 'week' | 'month' | 'quarter' | 'year'>('all');
  const [reportPeriod, setReportPeriod] = useState<'today' | 'week' | 'month' | null>(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const filterEntries = (entries: BowelMovement[], filter: string): BowelMovement[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return entries.filter(entry => {
      const entryDate = new Date(entry.date);
      
      switch (filter) {
        case 'today':
          return entryDate >= today;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return entryDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return entryDate >= monthAgo;
        case 'quarter':
          const quarterAgo = new Date(today);
          quarterAgo.setMonth(quarterAgo.getMonth() - 3);
          return entryDate >= quarterAgo;
        case 'year':
          const yearAgo = new Date(today);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          return entryDate >= yearAgo;
        default:
          return true;
      }
    });
  };

  const handleGenerateReport = (period: 'today' | 'week' | 'month') => {
    setReportPeriod(period);
    setHistoryFilter(period);
    
    // Filtruj wpisy dla wybranego okresu
    const filteredEntries = filterEntries(entries, period);
    
    // Generuj nazwę pliku
    const now = new Date();
    let filename = 'raport_wyproznien';
    let periodLabel = '';
    
    switch (period) {
      case 'today':
        periodLabel = 'dzisiejszy';
        filename = `raport_wyproznien_dzisiejszy_${now.toISOString().split('T')[0]}`;
        break;
      case 'week':
        periodLabel = 'tygodniowy';
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - 7);
        filename = `raport_wyproznien_tygodniowy_${weekStart.toISOString().split('T')[0]}_${now.toISOString().split('T')[0]}`;
        break;
      case 'month':
        periodLabel = 'miesięczny';
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        filename = `raport_wyproznien_miesieczny_${monthStart.toISOString().split('T')[0]}_${now.toISOString().split('T')[0]}`;
        break;
    }
    
    // Generuj raport PDF
    if (filteredEntries.length > 0) {
      exportService.exportToPDF(filteredEntries, filename);
    } else {
      toast.error(`Brak danych do wygenerowania raportu ${periodLabel}.`);
    }
  };

  const ReportButton = ({ period, label }: { period: 'today' | 'week' | 'month'; label: string }) => (
    <button
      onClick={() => handleGenerateReport(period)}
      style={{
        padding: '12px 16px',
        borderRadius: 10,
        border: reportPeriod === period ? '2px solid #6B7FFF' : '1px solid #e2e8f0',
        background: reportPeriod === period ? '#f0f4ff' : 'white',
        textAlign: 'left',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        if (reportPeriod !== period) {
          e.currentTarget.style.background = '#f8fafc';
          e.currentTarget.style.borderColor = '#cbd5e1';
        }
      }}
      onMouseLeave={(e) => {
        if (reportPeriod !== period) {
          e.currentTarget.style.background = 'white';
          e.currentTarget.style.borderColor = '#e2e8f0';
        }
      }}
    >
      <ReportIcon />
      <span style={{ fontSize: 14, fontWeight: 500, color: reportPeriod === period ? '#6B7FFF' : '#475569' }}>
        {label}
      </span>
    </button>
  );

  if (loadError && onRetry) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 md:space-y-6 px-0 md:px-0">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Wypróżnienia</h1>
          <p className="text-sm text-gray-600 mb-4">Dodaj nowe wypróżnienie i przeglądaj historię</p>
        </div>
        <ErrorWithRetry
          message={getErrorMessage(loadError)}
          onRetry={onRetry}
        />
      </div>
    );
  }

  const isDesktop = windowWidth >= 1024;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header: tytuł + raporty (tylko desktop) */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#0f172a', margin: 0 }}>
          Wypróżnienia
        </h1>
        {isDesktop && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <ReportButton period="today" label="Raport dzisiejszy" />
            <ReportButton period="week" label="Raport tygodniowy" />
            <ReportButton period="month" label="Raport miesięczny" />
          </div>
        )}
      </div>

      {isDesktop ? (
        /* Desktop: 2 kolumny - form | historia */
        <div
          style={{
            display: 'grid',
            gap: 24,
            alignItems: 'start',
          }}
        >
          <div ref={formRef}>
            <AddBowelMovementForm onSubmit={onSubmit} />
          </div>
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 20,
              border: '1px solid #e2e8f0',
            }}
          >
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 mb-4 md:mb-6">
              <h2 className="text-base md:text-lg font-semibold text-gray-900">Historia</h2>
              <select
                value={historyFilter}
                onChange={(e) => setHistoryFilter(e.target.value as any)}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white text-gray-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-blue"
              >
                <option value="all">Wszystkie</option>
                <option value="today">Dzisiaj</option>
                <option value="week">Ten tydzień</option>
                <option value="month">Ten miesiąc</option>
                <option value="quarter">Ten kwartał</option>
                <option value="year">Ten rok</option>
              </select>
            </div>
            <BowelMovementHistory
              entries={filterEntries(entries, historyFilter)}
              onUpdate={onUpdate}
              onDelete={onDelete}
              filter={historyFilter}
              onAddFirst={() => formRef.current?.scrollIntoView({ behavior: 'smooth' })}
            />
          </div>
        </div>
      ) : (
        /* Mobile: Formularz → Raporty → Historia */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div ref={formRef}>
            <AddBowelMovementForm onSubmit={onSubmit} />
          </div>
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 16,
              border: '1px solid #e2e8f0',
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 12 }}>
              Raporty
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <ReportButton period="today" label="Raport dzisiejszy" />
              <ReportButton period="week" label="Raport tygodniowy" />
              <ReportButton period="month" label="Raport miesięczny" />
            </div>
          </div>
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 16,
              border: '1px solid #e2e8f0',
            }}
          >
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 mb-4">
              <h2 className="text-base font-semibold text-gray-900">Historia</h2>
              <select
                value={historyFilter}
                onChange={(e) => setHistoryFilter(e.target.value as any)}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white text-gray-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-blue"
              >
                <option value="all">Wszystkie</option>
                <option value="today">Dzisiaj</option>
                <option value="week">Ten tydzień</option>
                <option value="month">Ten miesiąc</option>
                <option value="quarter">Ten kwartał</option>
                <option value="year">Ten rok</option>
              </select>
            </div>
            <BowelMovementHistory
              entries={filterEntries(entries, historyFilter)}
              onUpdate={onUpdate}
              onDelete={onDelete}
              filter={historyFilter}
              onAddFirst={() => formRef.current?.scrollIntoView({ behavior: 'smooth' })}
            />
          </div>
        </div>
      )}
    </div>
  );
};
